import type { IconType } from '@graftcode/design-system'

import { getTechnologyStackIcon } from '@common/constants/technologyStackIcons.const'

import { ONBOARDING_AGENT_CONTENT_STACKS_BY_DIR } from './generated/onboarding-agent-stacks.generated'
import { ONBOARDING_CONTENT_STACKS_BY_DIR } from './generated/onboarding-content-stacks.generated'
import {
  GRAFTCODE_ONBOARDING_SCENARIOS,
  GRAFTCODE_ONBOARDING_SETUP_MODE,
  type GraftcodeOnboardingScenarioId,
  type GraftcodeOnboardingSetupMode,
} from './graftcode-onboarding.const'

export type GraftcodeOnboardingStackOption = {
  id: string
  label: string
  icon?: IconType
}

const STACK_LABELS: Record<string, string> = {
  angular: 'Angular',
  astro: 'Astro',
  nextjs: 'Next.js',
  preact: 'Preact',
  qwik: 'Qwik',
  react: 'React',
  solidjs: 'SolidJS',
  svelte: 'Svelte',
  vanillajs: 'Vanilla JS',
  vue: 'Vue',
  webassembly: 'WebAssembly',
  javascript: 'JavaScript',
  dotnet: 'C#',
  python: 'Python',
  java: 'Java',
  kotlin: 'Kotlin',
  groovy: 'Groovy',
}

const FRONTEND_ONLY_LABELS: Record<string, string> = {
  vanillajs: 'Vanilla JS',
  webassembly: 'WebAssembly',
}

const STACK_SORT_PRIORITY: Record<string, number> = {
  angular: 0,
  astro: 1,
  nextjs: 2,
  preact: 3,
  qwik: 4,
  react: 5,
  solidjs: 6,
  svelte: 7,
  vanillajs: 8,
  vue: 9,
  webassembly: 10,
  javascript: 0,
  dotnet: 1,
  python: 2,
  java: 3,
  kotlin: 4,
  groovy: 5,
}

export function getStackLabel(stackId: string, scenarioId?: GraftcodeOnboardingScenarioId): string {
  if (scenarioId === 'frontend') {
    return FRONTEND_ONLY_LABELS[stackId] ?? STACK_LABELS[stackId] ?? stackId
  }
  if (scenarioId) {
    return STACK_LABELS[stackId] ?? stackId
  }
  return FRONTEND_ONLY_LABELS[stackId] ?? STACK_LABELS[stackId] ?? stackId
}

export function getStackIcon(stackId: string): IconType | undefined {
  return getTechnologyStackIcon(stackId)
}

export function sortStackIds(stackIds: string[]): string[] {
  return [...stackIds].sort((left, right) => (STACK_SORT_PRIORITY[left] ?? 99) - (STACK_SORT_PRIORITY[right] ?? 99))
}

/** Stack count on the frontend scenario -" at or above this, use full-width scroll picker. */
export const GRAFTCODE_ONBOARDING_STACK_PICKER_FULL_COUNT =
  ONBOARDING_CONTENT_STACKS_BY_DIR['1-connect-frontend-to-backend']?.length ?? 11

export function shouldUseFullWidthStackPicker(stackCount: number): boolean {
  return stackCount >= GRAFTCODE_ONBOARDING_STACK_PICKER_FULL_COUNT
}

function stackOptionsForScenario(
  scenarioId: GraftcodeOnboardingScenarioId,
  setupMode: GraftcodeOnboardingSetupMode = GRAFTCODE_ONBOARDING_SETUP_MODE.manual
): GraftcodeOnboardingStackOption[] {
  const scenario = GRAFTCODE_ONBOARDING_SCENARIOS.find((item) => item.id === scenarioId)
  if (!scenario) {
    return []
  }
  const stackMap =
    setupMode === GRAFTCODE_ONBOARDING_SETUP_MODE.agent
      ? ONBOARDING_AGENT_CONTENT_STACKS_BY_DIR
      : ONBOARDING_CONTENT_STACKS_BY_DIR
  const stackIds = stackMap[scenario.contentDir] ?? []
  return sortStackIds([...stackIds]).map((id) => ({
    id,
    label: getStackLabel(id, scenarioId),
    icon: getStackIcon(id),
  }))
}

/** Built from synced onboarding markdown -" prefer `getOnboardingStacksForScenario`. */
export const GRAFTCODE_ONBOARDING_STACKS_BY_SCENARIO: Record<
  GraftcodeOnboardingScenarioId,
  GraftcodeOnboardingStackOption[]
> = GRAFTCODE_ONBOARDING_SCENARIOS.reduce(
  (accumulator, scenario) => {
    accumulator[scenario.id] = stackOptionsForScenario(scenario.id)
    return accumulator
  },
  {} as Record<GraftcodeOnboardingScenarioId, GraftcodeOnboardingStackOption[]>
)

export const GRAFTCODE_ONBOARDING_AGENT_STACKS_BY_SCENARIO: Record<
  GraftcodeOnboardingScenarioId,
  GraftcodeOnboardingStackOption[]
> = GRAFTCODE_ONBOARDING_SCENARIOS.reduce(
  (accumulator, scenario) => {
    accumulator[scenario.id] = stackOptionsForScenario(scenario.id, GRAFTCODE_ONBOARDING_SETUP_MODE.agent)
    return accumulator
  },
  {} as Record<GraftcodeOnboardingScenarioId, GraftcodeOnboardingStackOption[]>
)
